import React, { useState, useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Container, Row, Col } from 'react-bootstrap';
import { FaMapMarkerAlt, FaEnvelope, FaPhone, FaFacebookF, FaTwitter, FaInstagram, FaYoutube, FaTiktok } from 'react-icons/fa';
import styled from 'styled-components';

const ContactPage = () => {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [formStatus, setFormStatus] = useState({
    isSubmitting: false,
    isSubmitted: false,
    error: null
  });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus({ ...formStatus, isSubmitting: true });
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      setFormStatus({ isSubmitting: false, isSubmitted: true, error: null });
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      setFormStatus({ isSubmitting: false, isSubmitted: false, error: 'Failed to send message' });
    }
  };

  return (
    <StyledContactPage ref={ref}>
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={controls}
          variants={{
            visible: { opacity: 1, y: 0 }
          }}
          transition={{ duration: 0.6 }}
          className="page-header"
        >
          <h1>Get in Touch</h1>
          <div className="header-accent"></div>
        </motion.div>

        <Row className="contact-content">
          <Col lg={5}>
            <motion.div
              className="contact-info-wrapper"
              initial={{ opacity: 0, x: -30 }}
              animate={controls}
              variants={{
                visible: { opacity: 1, x: 0 }
              }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="contact-intro">
                <h2>Let's Connect</h2>
                <p>Reach out to us for any inquiries about tourism in Afar Region.</p>
              </div>

              <div className="timeline">
                {[
                  { icon: <FaMapMarkerAlt />, title: 'Visit Us', content: 'Mekelle, Tigray', delay: 0.3 },
                  { icon: <FaEnvelope />, title: 'Email Us', content: 'tigrai.tourism@ethionet.et', delay: 0.4 },
                  { icon: <FaPhone />, title: 'Call Us', content: '+251344409360', delay: 0.5 }
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    className="timeline-item"
                    initial={{ opacity: 0, x: -20 }}
                    animate={controls}
                    variants={{
                      visible: { opacity: 1, x: 0 }
                    }}
                    transition={{ duration: 0.6, delay: item.delay }}
                  >
                    <div className="timeline-icon">{item.icon}</div>
                    <div className="timeline-content">
                      <h3>{item.title}</h3>
                      <p>{item.content}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="social-section">
                <h3>Follow Us</h3>
                <div className="social-icons">
                  {[FaFacebookF, FaTwitter, FaInstagram, FaYoutube, FaTiktok].map((Icon, index) => (
                    <motion.div
                      key={index}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Icon />
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </Col>

          <Col lg={7}>
            <motion.div
              className="form-wrapper"
              initial={{ opacity: 0, x: 30 }}
              animate={controls}
              variants={{
                visible: { opacity: 1, x: 0 }
              }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <form onSubmit={handleSubmit}>
                <div className="form-grid">
                  <motion.div
                    className="input-group"
                    whileFocus={{ scale: 1.02 }}
                  >
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Your Name"
                    />
                  </motion.div>

                  <motion.div
                    className="input-group"
                    whileFocus={{ scale: 1.02 }}
                  >
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="Your Email"
                    />
                  </motion.div>

                  <motion.div
                    className="input-group"
                    whileFocus={{ scale: 1.02 }}
                  >
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Your Phone"
                    />
                  </motion.div>

                  <motion.div
                    className="input-group"
                    whileFocus={{ scale: 1.02 }}
                  >
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder="Subject"
                    />
                  </motion.div>
                </div>

                <motion.div
                  className="input-group"
                  whileFocus={{ scale: 1.02 }}
                >
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder="Your Message"
                    rows={6}
                  ></textarea>
                </motion.div>

                <motion.button
                  type="submit"
                  disabled={formStatus.isSubmitting}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {formStatus.isSubmitting ? 'Sending...' : 'Send Message'}
                </motion.button>

                {formStatus.isSubmitted && (
                  <motion.div
                    className="success-message"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    Message sent successfully!
                  </motion.div>
                )}

                {formStatus.error && (
                  <motion.div
                    className="error-message"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    {formStatus.error}
                  </motion.div>
                )}
              </form>
            </motion.div>
          </Col>
        </Row>
      </Container>
    </StyledContactPage>
  );
};

const StyledContactPage = styled.div`
  padding: 120px 0;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at 100% 0%, rgba(45, 123, 168, 0.05) 0%, transparent 50%);
    pointer-events: none;
  }

  .page-header {
    text-align: center;
    margin-bottom: 60px;

    h1 {
      font-size: 3.5rem;
      font-weight: 700;
      color: #1e293b;
      margin-bottom: 20px;
    }

    .header-accent {
      width: 80px;
      height: 4px;
      background: linear-gradient(45deg, #2d7ba8, #34495e);
      margin: 0 auto;
      border-radius: 2px;
    }
  }

  .contact-content {
    background: white;
    border-radius: 30px;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.1);
    overflow: hidden;
  }

  .contact-info-wrapper {
    padding: 40px;
    background: linear-gradient(135deg, #2d7ba8, #34495e);
    height: 100%;
    color: white;

    .contact-intro {
      margin-bottom: 40px;

      h2 {
        font-size: 2rem;
        margin-bottom: 15px;
      }

      p {
        opacity: 0.9;
      }
    }

    .timeline {
      margin-bottom: 40px;

      .timeline-item {
        display: flex;
        align-items: flex-start;
        margin-bottom: 30px;
        padding-left: 20px;
        position: relative;

        &::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          height: 100%;
          width: 2px;
          background: rgba(255, 255, 255, 0.2);
        }

        .timeline-icon {
          background: rgba(255, 255, 255, 0.1);
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 20px;
          flex-shrink: 0;
        }

        .timeline-content {
          h3 {
            font-size: 1.1rem;
            margin-bottom: 5px;
          }

          p {
            opacity: 0.9;
            margin: 0;
          }
        }
      }
    }

    .social-section {
      h3 {
        margin-bottom: 20px;
      }

      .social-icons {
        display: flex;
        gap: 15px;

        div {
          background: rgba(255, 255, 255, 0.1);
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;

          &:hover {
            background: rgba(255, 255, 255, 0.2);
          }
        }
      }
    }
  }

  .form-wrapper {
    padding: 40px;

    .form-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 20px;
      margin-bottom: 20px;
    }

    .input-group {
      margin-bottom: 20px;

      input, textarea {
        width: 100%;
        padding: 15px;
        border: 1px solid #e2e8f0;
        border-radius: 10px;
        font-size: 1rem;
        transition: all 0.3s ease;

        &:focus {
          outline: none;
          border-color: #2d7ba8;
          box-shadow: 0 0 0 3px rgba(45, 123, 168, 0.1);
        }
      }

      textarea {
        resize: vertical;
      }
    }

    button {
      width: 100%;
      padding: 15px;
      background: linear-gradient(45deg, #2d7ba8, #34495e);
      color: white;
      border: none;
      border-radius: 10px;
      font-size: 1.1rem;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.3s ease;

      &:disabled {
        opacity: 0.7;
        cursor: not-allowed;
      }
    }

    .success-message {
      margin-top: 20px;
      padding: 15px;
      background: #dcfce7;
      color: #166534;
      border-radius: 10px;
      text-align: center;
    }

    .error-message {
      margin-top: 20px;
      padding: 15px;
      background: #fee2e2;
      color: #991b1b;
      border-radius: 10px;
      text-align: center;
    }
  }

  @media (max-width: 992px) {
    padding: 80px 0;

    .page-header h1 {
      font-size: 2.5rem;
    }

    .contact-content {
      flex-direction: column;
    }

    .form-wrapper .form-grid {
      grid-template-columns: 1fr;
    }
  }
`;

export default ContactPage;